import { useEffect, useState, type FormEvent } from 'react';
import type { MeDto } from '@setu/shared';
import { api, apiErrorText } from '../api.ts';

/**
 * "Forgot your password?" — asks for the address and always says the same
 * thing afterwards, because the server will not say whether an account exists.
 */
export function ForgotPasswordForm({ onBack }: { onBack: () => void }): React.ReactElement {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="stack">
        <p className="notice mint" role="status">
          If there is a Setu account for <b>{email}</b>, a link to choose a new password is on its way. It works
          once, for the next hour. Check your spam folder if it does not turn up.
        </p>
        <div>
          <button className="linklike" onClick={onBack}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="stack" onSubmit={submit}>
      <p className="small muted">Tell us the email you use for Setu and we will send you a link to choose a new password.</p>
      <div className="field">
        <label htmlFor="forgot-email">Email</label>
        <input
          id="forgot-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}
      <button className="btn block" type="submit" disabled={busy}>
        {busy ? 'One moment…' : 'Email me a link'}
      </button>
      <div>
        <button type="button" className="linklike" onClick={onBack}>
          Back to sign in
        </button>
      </div>
    </form>
  );
}

/** Where the emailed link lands: choose a new password, and you are signed in. */
export function ResetPassword({
  token,
  onDone,
  onGiveUp,
}: {
  token: string;
  onDone: (user: MeDto) => void;
  onGiveUp: () => void;
}): React.ReactElement {
  const [valid, setValid] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .checkReset(token)
      .then((res) => setValid(res.valid))
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < 10) return setError('Use at least 10 characters.');
    if (password !== again) return setError('Those two do not match.');
    setBusy(true);
    setError(null);
    try {
      const { user } = await api.resetPassword(token, password);
      onDone(user);
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <div className="card stack">
        <div>
          <div className="brand" style={{ paddingLeft: 0 }}>
            <b>Setu</b>
          </div>
          <h1 style={{ marginTop: 14 }}>Choose a new password.</h1>
        </div>

        {valid === null && <p className="muted">One moment…</p>}

        {valid === false && (
          <div className="stack">
            <p className="notice alert" role="alert">
              This link has expired or has already been used. Links work once, for an hour.
            </p>
            <div>
              <button className="linklike" onClick={onGiveUp}>
                Back to sign in, to ask for a new one
              </button>
            </div>
          </div>
        )}

        {valid && (
          <form className="stack" onSubmit={submit}>
            <div className="field">
              <label htmlFor="reset-password">New password</label>
              <input
                id="reset-password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="small muted">At least 10 characters.</p>
            </div>
            <div className="field">
              <label htmlFor="reset-again">And once more</label>
              <input
                id="reset-again"
                type="password"
                autoComplete="new-password"
                required
                value={again}
                onChange={(e) => setAgain(e.target.value)}
              />
            </div>
            {error && (
              <p className="notice alert" role="alert">
                {error}
              </p>
            )}
            <button className="btn block" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save and sign in'}
            </button>
            <p className="small muted">This signs you out everywhere else.</p>
          </form>
        )}
      </div>
    </div>
  );
}
